import { CustomPageConfigurationType, DefaultCarouselView, DefaultPageConfigurationType, DefaultPageType } from '../../../../../../../api/custom-pages.dao';
import {
    convertBoolToDefaultOrCustomTypeForMixPanel,
    convertBoolTypeForMixPanel,
    isTrackingFieldEnabled,
    OpenFrom,
    TRACKING_EVENTS
} from '../../../../../../../tracking-service/tracking-values';

type PageConfigValueMap = { [key: string]: string | {} };

const agendaPageConfigValueMap: PageConfigValueMap = {
    enabled: 'My Schedule Page Enabled After Sign In',
    label: {
        label:'My Schedule Label',
        defaultValue:'my schedule'
    },
    pageLinkEnabled: 'Show link on Welcome page',
    linkDescription: 'My Schedule Description',
    topicEnabled: 'Display Meeting Topic for all "Closed" meetings on My Schedule',
    restrictAccessEnabled: 'Restrict Access to My Schedule page',
};

const generalSessionPageConfigValueMap: PageConfigValueMap = {
    enabled: 'General Sessions display After Sign-In',
    publicEnabled: 'General Sessions display Public',
    label: {
        label:'General Sessions Page Label',
        defaultValue:'general sessions'
    },
    hostAttendeesEnabled: 'Display Host Attendees',
    pageLinkEnabled: 'Show link on Welcome page',
    linkDescription: 'General Sessions Description',
    restrictAccessEnabled: 'Restrict Access to General Sessions page',
    preRegistrationEnabled: 'Enforce pre-registration before attendees can join sessions',
    subscribeOnRegistrationEnabled: 'Subscribe to General Sessions on Registration',
    pastSessionsEnabled: 'Display Past Sessions',
    layout: 'General Sessions Layout',
    defaultCarouselView: 'Default Configuration',
    subscribeAttendeeToMeetingOnRegistrationEnabled: 'Subscribe attendee to meeting on registration'
};

const showcasePageConfigValueMap: PageConfigValueMap = {
    enabled: 'Showcase toggle',
    label: 'Showcase Page Label',
    pageLinkEnabled: 'Show link on Welcome page',
    linkDescription: 'Showcase Description',
    embedCodeId: 'Showcase Embed Code Id'
};

const supportPageConfigValueMap: PageConfigValueMap = {
    enabled: 'Support toggle',
    label: 'Support Page Label',
    pageLinkEnabled: 'Show link on Welcome page',
    linkDescription: 'Support Description',
    supportInformation: 'Support Information'
};

const welcomePageConfigValueMap: PageConfigValueMap = {
    enabled: 'Welcome Page Enabled After Sign In',
    label: {
        label: 'Welcome Page Label',
        defaultValue: 'welcome',
    },
    welcomeKnovioEnabled: 'Welcome Knovio toggle',
    welcomeKnovioEmbedId: 'Embed Code Id',
    calendarButtonEnabled: 'Show "Add to Calendar" button'
};

const speakersPageConfigValueMap: PageConfigValueMap = {
    enabled: 'Speakers display After Sign In',
    label: 'Speakers Page Label',
    publicEnabled: 'Speakers display Public',
    pageLinkEnabled: 'Show link on Welcome page',
    linkDescription: 'Speakers Description'
};

const replaysPageConfigValueMap: PageConfigValueMap = {
    enabled: 'Replays display After Sign In',
    pageLinkEnabled: 'Show link on Welcome page',
    linkDescription: 'Replays Description',
    replaysNotes: 'Replays Notes',
    upcomingSessionsEnabled: 'Display Upcoming Sessions',
    hostAttendeesEnabled: 'Display Host Attendees',
    replaysLayout: 'Replays Layout',
    replaysDefaultCarouselView: 'Default Configuration',
    upcomingSessionType: 'Upcoming Sessions Option',
    replaysPreRegistrationEnabled: 'Enforced pre-registration before attendees can join sessions',
    restrictAccessEnabled: 'Restrict Access to Replays page',
};

const carouselViewValues = {
    [DefaultCarouselView.ShowAll]: 'Show All',
    [DefaultCarouselView.Collapsed]: 'Collapsed'
};


export class MixPanelTrackingDefaultPagesMapper {
    public static dataToTrackingEvent(
        dto: DefaultPageConfigurationType | CustomPageConfigurationType,
        initialProps: Partial<DefaultPageConfigurationType | CustomPageConfigurationType>): {
            eventName: string;
            eventData: Record<string, 'Yes' | 'No' | string>;
        } {
        const eventData = {};
        const pagesConfig = {
            [DefaultPageType.Showcase]: {
                eventName: TRACKING_EVENTS.SHOWCASE_PAGE_CONFIGURATION,
                valueMap: showcasePageConfigValueMap
            },
            [DefaultPageType.Agenda]: {
                eventName: TRACKING_EVENTS.MY_SCHEDULE_PAGE_CONFIGURATION,
                valueMap: agendaPageConfigValueMap
            },
            [DefaultPageType.Session]: {
                eventName: TRACKING_EVENTS.GENERAL_SESSIONS_PAGE_CONFIGURATION,
                valueMap: generalSessionPageConfigValueMap
            },
            [DefaultPageType.Support]: {
                eventName: TRACKING_EVENTS.SUPPORT_PAGE_CONFIGURATION,
                valueMap: supportPageConfigValueMap
            },
            [DefaultPageType.Welcome]: {
                eventName: TRACKING_EVENTS.WELCOME_PAGE_CONFIGURATION,
                valueMap: welcomePageConfigValueMap
            },
            [DefaultPageType.Speakers]: {
                eventName: TRACKING_EVENTS.SPEAKERS_PAGE_CONFIGURATION,
                valueMap: speakersPageConfigValueMap
            },
            [DefaultPageType.Replays]: {
                eventName: TRACKING_EVENTS.REPLAYS_PAGE_CONFIGURATION,
                valueMap: replaysPageConfigValueMap
            }
        }[dto.type];

        const setDefaultTrackingProps = (k: string, key: string) => {
            switch (typeof dto[k]) {
                case 'boolean':
                    eventData[key] = isTrackingFieldEnabled(dto[k]);
                    break;
                case 'string':
                    eventData[key] = convertBoolTypeForMixPanel(!!dto[k]);
                    break;
            }
        };

        const setReplaysTrackingProps = () => {
            for (const k of trackingKeys) {
                const key = pagesConfig.valueMap[k];
                const exceptions = ['replaysLayout', 'replaysPreRegistrationEnabled', 'upcomingSessionType', 'replaysDefaultCarouselView'];

                if (exceptions.indexOf(k) === -1) {
                    setDefaultTrackingProps(k, key);
                } else {
                    switch (k) {
                        case 'replaysLayout':
                            eventData[key] = dto[k];
                            break;
                        case 'upcomingSessionType':
                            eventData[key] = dto['upcomingSessionsEnabled'] ? dto[k] : 'N/A';
                            break;
                        case 'replaysPreRegistrationEnabled':
                            eventData[key] = dto['upcomingSessionsEnabled'] ? isTrackingFieldEnabled(dto[k]) : 'N/A';
                            break;
                        case 'replaysDefaultCarouselView':
                            eventData[key] = carouselViewValues[dto[k] ?? DefaultCarouselView.ShowAll];
                            break;
                    }
                }
            }
        };

        const trackingKeys = Object.keys(pagesConfig.valueMap);
        const sessionExceptionFields = ['layout', 'defaultCarouselView'];

        if (pagesConfig.eventName === TRACKING_EVENTS.REPLAYS_PAGE_CONFIGURATION) {
            setReplaysTrackingProps();
        } else {
            for (const k of trackingKeys) {
                const key = pagesConfig.valueMap[k];
                let defaultValue = '';
                // Check for the defaultValue if it exist for label grab it for comparision with current value.
                if ('label' === k && pagesConfig.valueMap[k].hasOwnProperty('defaultValue')){
                    defaultValue = pagesConfig.valueMap[k]['defaultValue'];
                }
                let value = dto[k] !== initialProps[k];

                // If label value as defaultValue then it is considered as default value
                if ('label' === k){
                    value = (dto[k].trim().toLowerCase() === defaultValue) ? true : false;
                }

                // If linkDescription value is empty then it is considered as default value
                if ('linkDescription' === k || 'welcomeKnovioEmbedId' === k){
                    value = !!dto[k];
                }

                if (typeof dto[k] === 'boolean') {
                    eventData[key] = isTrackingFieldEnabled(dto[k]);
                } else if (key.label === generalSessionPageConfigValueMap.label['label'] ||
                    key.label === agendaPageConfigValueMap.label['label'] ||
                    key.label === welcomePageConfigValueMap.label['label']
                ) {
                    eventData[key.label] = convertBoolToDefaultOrCustomTypeForMixPanel(value);
                } else {
                    if (pagesConfig.eventName === TRACKING_EVENTS.GENERAL_SESSIONS_PAGE_CONFIGURATION && sessionExceptionFields.indexOf(k) !== -1) {
                        eventData[key] = k === 'defaultCarouselView' ? carouselViewValues[dto[k]] : dto[k];
                    } else if (k === 'linkDescription') {
                        eventData[key] = convertBoolTypeForMixPanel(Boolean(dto[k]));
                    } else {
                        eventData[key] = convertBoolTypeForMixPanel(value);
                    }
                }
            }
            eventData['Open From'] = OpenFrom.Passport;
        }

        return ({
            eventData: eventData,
            eventName: pagesConfig.eventName,
        });
    }
}